import { lazy, Suspense, useEffect, useState } from 'react';
import { BrowserRouter, Routes, Route, useLocation } from 'react-router-dom';
import { AnimatePresence, motion } from 'framer-motion';
import { Header } from '@/components/layout/Header';
import { Footer } from '@/components/layout/Footer';
import { AdminLayout } from '@/components/layout/AdminLayout';
import { CartDrawer } from '@/components/overlays/CartDrawer';
import { SearchDialog } from '@/components/overlays/SearchDialog';
import { QuickViewModal } from '@/components/overlays/QuickViewModal';
import { ProductCompare } from '@/components/overlays/ProductCompare';
import { NewsletterPopup } from '@/components/overlays/NewsletterPopup';
import { SocialProofPopup } from '@/components/overlays/SocialProofPopup';
import { AbandonedCartRecovery } from '@/components/overlays/AbandonedCartRecovery';
import { CookieConsent } from '@/components/overlays/CookieConsent';
import { WhatsAppButton } from '@/components/overlays/WhatsAppButton';
import { BackToTop } from '@/components/overlays/BackToTop';
import { useCart } from '@/lib/cart';
import { refresh } from '@/lib/auth';

const Home = lazy(() => import('@/pages/customer/Home'));
const Shop = lazy(() => import('@/pages/customer/Shop'));
const Category = lazy(() => import('@/pages/customer/Category'));
const Product = lazy(() => import('@/pages/customer/Product'));
const Search = lazy(() => import('@/pages/customer/Search'));
const Cart = lazy(() => import('@/pages/customer/Cart'));
const Checkout = lazy(() => import('@/pages/customer/Checkout'));
const OrderSuccess = lazy(() => import('@/pages/customer/OrderSuccess'));
const TrackOrder = lazy(() => import('@/pages/customer/TrackOrder'));
const Login = lazy(() => import('@/pages/customer/Login'));
const Register = lazy(() => import('@/pages/customer/Register'));
const ForgotPassword = lazy(() => import('@/pages/customer/ForgotPassword'));
const Account = lazy(() => import('@/pages/customer/Account'));
const OrderHistory = lazy(() => import('@/pages/customer/OrderHistory'));
const OrderDetail = lazy(() => import('@/pages/customer/OrderDetail'));
const Wishlist = lazy(() => import('@/pages/customer/Wishlist'));
const Addresses = lazy(() => import('@/pages/customer/Addresses'));
const Profile = lazy(() => import('@/pages/customer/Profile'));
const Loyalty = lazy(() => import('@/pages/customer/Loyalty'));
const Referral = lazy(() => import('@/pages/customer/Referral'));
const Notifications = lazy(() => import('@/pages/customer/Notifications'));
const Deals = lazy(() => import('@/pages/customer/Deals'));
const Bundles = lazy(() => import('@/pages/customer/Bundles'));
const GiftCards = lazy(() => import('@/pages/customer/GiftCards'));
const BlogList = lazy(() => import('@/pages/customer/BlogList'));
const BlogPost = lazy(() => import('@/pages/customer/BlogPost'));
const About = lazy(() => import('@/pages/customer/About'));
const Contact = lazy(() => import('@/pages/customer/Contact'));
const FAQ = lazy(() => import('@/pages/customer/FAQ'));
const ShippingPolicy = lazy(() => import('@/pages/customer/ShippingPolicy'));
const ReturnPolicy = lazy(() => import('@/pages/customer/ReturnPolicy'));
const PrivacyPolicy = lazy(() => import('@/pages/customer/PrivacyPolicy'));
const Terms = lazy(() => import('@/pages/customer/Terms'));
const NotFound = lazy(() => import('@/pages/customer/NotFound'));

const AdminDashboard = lazy(() => import('@/pages/admin/Dashboard'));
const AdminOrders = lazy(() => import('@/pages/admin/Orders'));
const AdminOrderDetail = lazy(() => import('@/pages/admin/OrderDetail'));
const AdminPaymentReview = lazy(() => import('@/pages/admin/PaymentReview'));
const AdminProducts = lazy(() => import('@/pages/admin/Products'));
const AdminProductForm = lazy(() => import('@/pages/admin/ProductForm'));
const AdminCategories = lazy(() => import('@/pages/admin/Categories'));
const AdminCustomers = lazy(() => import('@/pages/admin/Customers'));
const AdminReviews = lazy(() => import('@/pages/admin/Reviews'));
const AdminCoupons = lazy(() => import('@/pages/admin/Coupons'));
const AdminBanners = lazy(() => import('@/pages/admin/Banners'));
const AdminContent = lazy(() => import('@/pages/admin/Content'));
const AdminSEO = lazy(() => import('@/pages/admin/SEO'));
const AdminAnalytics = lazy(() => import('@/pages/admin/Analytics'));
const AdminSettings = lazy(() => import('@/pages/admin/Settings'));

function PageTransition({ children }: { children: React.ReactNode }) {
  const location = useLocation();
  return (
    <AnimatePresence mode="wait">
      <motion.div
        key={location.pathname}
        initial={{ opacity: 0, y: 12 }}
        animate={{ opacity: 1, y: 0 }}
        exit={{ opacity: 0, y: -12 }}
        transition={{ duration: 0.2, ease: 'easeOut' }}
      >
        {children}
      </motion.div>
    </AnimatePresence>
  );
}

function CustomerLayout({ children }: { children: React.ReactNode }) {
  const [searchOpen, setSearchOpen] = useState(false);
  const [darkMode, setDarkMode] = useState(() => {
    const stored = localStorage.getItem('sggadgets_dark_mode');
    if (stored !== null) return stored === 'true';
    return window.matchMedia('(prefers-color-scheme: dark)').matches;
  });

  const cartState = useCart();

  useEffect(() => {
    document.documentElement.classList.toggle('dark', darkMode);
    localStorage.setItem('sggadgets_dark_mode', String(darkMode));
  }, [darkMode]);

  return (
    <div className="min-h-screen flex flex-col bg-background dark:bg-gray-950 text-foreground dark:text-white">
      <Header
        onSearchOpen={() => setSearchOpen(true)}
        onCartOpen={() => cartState.setOpen(true)}
        darkMode={darkMode}
        onToggleDarkMode={() => setDarkMode((d) => !d)}
      />
      <main className="flex-1">
        <PageTransition>{children}</PageTransition>
      </main>
      <Footer />
      <CartDrawer isOpen={cartState.isOpen} onClose={() => cartState.setOpen(false)} onCheckout={() => { cartState.setOpen(false); window.location.href = '/checkout'; }} />
      <SearchDialog isOpen={searchOpen} onClose={() => setSearchOpen(false)} />
      <QuickViewModal isOpen={false} onClose={() => {}} product={null} />
      <ProductCompare products={[]} onRemove={() => {}} onClose={() => {}} />
      <NewsletterPopup onSubscribe={() => {}} />
      <SocialProofPopup />
      <AbandonedCartRecovery cartItems={cartState.items} subtotal={cartState.getSummary().subtotal} onCheckout={() => { cartState.setOpen(false); window.location.href = '/checkout'; }} />
      <CookieConsent />
      <WhatsAppButton />
      <BackToTop />
    </div>
  );
}

function AdminRoute({ children }: { children: React.ReactNode }) {
  return (
    <div className="min-h-screen bg-gray-100 dark:bg-gray-950">
      <AdminLayout>{children}</AdminLayout>
    </div>
  );
}

function LoadingFallback() {
  return (
    <div className="flex items-center justify-center min-h-[60vh]">
      <div className="flex flex-col items-center gap-4">
        <div className="w-10 h-10 border-4 border-primary-600 border-t-transparent rounded-full animate-spin" />
        <p className="text-sm text-muted-foreground">Loading...</p>
      </div>
    </div>
  );
}

function AuthRefresh() {
  useEffect(() => {
    const refreshToken = localStorage.getItem('sggadgets_refresh_token');
    if (refreshToken) {
      refresh().catch(() => {});
    }
  }, []);
  return null;
}

export default function App() {
  return (
    <BrowserRouter>
      <AuthRefresh />
      <Suspense fallback={<LoadingFallback />}>
        <Routes>
          <Route
            path="/"
            element={
              <CustomerLayout>
                <Home />
              </CustomerLayout>
            }
          />
          <Route
            path="/shop"
            element={
              <CustomerLayout>
                <Shop />
              </CustomerLayout>
            }
          />
          <Route
            path="/category/:slug"
            element={
              <CustomerLayout>
                <Category />
              </CustomerLayout>
            }
          />
          <Route
            path="/product/:slug"
            element={
              <CustomerLayout>
                <Product />
              </CustomerLayout>
            }
          />
          <Route
            path="/search"
            element={
              <CustomerLayout>
                <Search />
              </CustomerLayout>
            }
          />
          <Route
            path="/cart"
            element={
              <CustomerLayout>
                <Cart />
              </CustomerLayout>
            }
          />
          <Route
            path="/checkout"
            element={
              <CustomerLayout>
                <Checkout />
              </CustomerLayout>
            }
          />
          <Route
            path="/order-success"
            element={
              <CustomerLayout>
                <OrderSuccess />
              </CustomerLayout>
            }
          />
          <Route
            path="/track-order"
            element={
              <CustomerLayout>
                <TrackOrder />
              </CustomerLayout>
            }
          />
          <Route
            path="/login"
            element={
              <CustomerLayout>
                <Login />
              </CustomerLayout>
            }
          />
          <Route
            path="/register"
            element={
              <CustomerLayout>
                <Register />
              </CustomerLayout>
            }
          />
          <Route
            path="/forgot-password"
            element={
              <CustomerLayout>
                <ForgotPassword />
              </CustomerLayout>
            }
          />
          <Route
            path="/account"
            element={
              <CustomerLayout>
                <Account />
              </CustomerLayout>
            }
          />
          <Route
            path="/account/orders"
            element={
              <CustomerLayout>
                <OrderHistory />
              </CustomerLayout>
            }
          />
          <Route
            path="/account/orders/:id"
            element={
              <CustomerLayout>
                <OrderDetail />
              </CustomerLayout>
            }
          />
          <Route
            path="/wishlist"
            element={
              <CustomerLayout>
                <Wishlist />
              </CustomerLayout>
            }
          />
          <Route
            path="/account/addresses"
            element={
              <CustomerLayout>
                <Addresses />
              </CustomerLayout>
            }
          />
          <Route
            path="/account/profile"
            element={
              <CustomerLayout>
                <Profile />
              </CustomerLayout>
            }
          />
          <Route
            path="/account/loyalty"
            element={
              <CustomerLayout>
                <Loyalty />
              </CustomerLayout>
            }
          />
          <Route
            path="/account/referral"
            element={
              <CustomerLayout>
                <Referral />
              </CustomerLayout>
            }
          />
          <Route
            path="/account/notifications"
            element={
              <CustomerLayout>
                <Notifications />
              </CustomerLayout>
            }
          />
          <Route
            path="/deals"
            element={
              <CustomerLayout>
                <Deals />
              </CustomerLayout>
            }
          />
          <Route
            path="/bundles"
            element={
              <CustomerLayout>
                <Bundles />
              </CustomerLayout>
            }
          />
          <Route
            path="/gift-cards"
            element={
              <CustomerLayout>
                <GiftCards />
              </CustomerLayout>
            }
          />
          <Route
            path="/blog"
            element={
              <CustomerLayout>
                <BlogList />
              </CustomerLayout>
            }
          />
          <Route
            path="/blog/:slug"
            element={
              <CustomerLayout>
                <BlogPost />
              </CustomerLayout>
            }
          />
          <Route
            path="/about"
            element={
              <CustomerLayout>
                <About />
              </CustomerLayout>
            }
          />
          <Route
            path="/contact"
            element={
              <CustomerLayout>
                <Contact />
              </CustomerLayout>
            }
          />
          <Route
            path="/faq"
            element={
              <CustomerLayout>
                <FAQ />
              </CustomerLayout>
            }
          />
          <Route
            path="/shipping-policy"
            element={
              <CustomerLayout>
                <ShippingPolicy />
              </CustomerLayout>
            }
          />
          <Route
            path="/return-policy"
            element={
              <CustomerLayout>
                <ReturnPolicy />
              </CustomerLayout>
            }
          />
          <Route
            path="/privacy-policy"
            element={
              <CustomerLayout>
                <PrivacyPolicy />
              </CustomerLayout>
            }
          />
          <Route
            path="/terms"
            element={
              <CustomerLayout>
                <Terms />
              </CustomerLayout>
            }
          />
          <Route
            path="/admin"
            element={
              <AdminRoute>
                <AdminDashboard />
              </AdminRoute>
            }
          />
          <Route
            path="/admin/orders"
            element={
              <AdminRoute>
                <AdminOrders />
              </AdminRoute>
            }
          />
          <Route
            path="/admin/orders/:id"
            element={
              <AdminRoute>
                <AdminOrderDetail />
              </AdminRoute>
            }
          />
          <Route
            path="/admin/payment-review"
            element={
              <AdminRoute>
                <AdminPaymentReview />
              </AdminRoute>
            }
          />
          <Route
            path="/admin/products"
            element={
              <AdminRoute>
                <AdminProducts />
              </AdminRoute>
            }
          />
          <Route
            path="/admin/products/new"
            element={
              <AdminRoute>
                <AdminProductForm />
              </AdminRoute>
            }
          />
          <Route
            path="/admin/products/:id/edit"
            element={
              <AdminRoute>
                <AdminProductForm />
              </AdminRoute>
            }
          />
          <Route
            path="/admin/categories"
            element={
              <AdminRoute>
                <AdminCategories />
              </AdminRoute>
            }
          />
          <Route
            path="/admin/customers"
            element={
              <AdminRoute>
                <AdminCustomers />
              </AdminRoute>
            }
          />
          <Route
            path="/admin/reviews"
            element={
              <AdminRoute>
                <AdminReviews />
              </AdminRoute>
            }
          />
          <Route
            path="/admin/coupons"
            element={
              <AdminRoute>
                <AdminCoupons />
              </AdminRoute>
            }
          />
          <Route
            path="/admin/banners"
            element={
              <AdminRoute>
                <AdminBanners />
              </AdminRoute>
            }
          />
          <Route
            path="/admin/content"
            element={
              <AdminRoute>
                <AdminContent />
              </AdminRoute>
            }
          />
          <Route
            path="/admin/seo"
            element={
              <AdminRoute>
                <AdminSEO />
              </AdminRoute>
            }
          />
          <Route
            path="/admin/analytics"
            element={
              <AdminRoute>
                <AdminAnalytics />
              </AdminRoute>
            }
          />
          <Route
            path="/admin/settings"
            element={
              <AdminRoute>
                <AdminSettings />
              </AdminRoute>
            }
          />
          <Route
            path="*"
            element={
              <CustomerLayout>
                <NotFound />
              </CustomerLayout>
            }
          />
        </Routes>
      </Suspense>
    </BrowserRouter>
  );
}
